import React from "react";
import { Link } from "react-router-dom";
import { websitePaths } from "../../../routes/websitePaths";
import { useFetchPublicProjectImagesQuery } from "../../../store/api/projectImagesApi";
import "../../../components/website/sections/ProjectCategoryPage.css";

const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='500'%3E%3Crect width='800' height='500' fill='%23e2e8f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2394a3b8' font-size='18' font-family='sans-serif'%3EDialin24 Industrial%3C/text%3E%3C/svg%3E";

type IndustrialProject = {
  title: string;
  category: string;
  location: string;
  description: string;
  features: string[];
  images: string[];
};

const categoryHighlights = [
  {
    title: "Manufacturing Facilities",
    description: "Industrial spaces planned for production flow, worker safety, and reliable operational use.",
    icon: "bi bi-building-gear",
  },
  {
    title: "Warehouse And Storage Projects",
    description: "Large-format warehouse projects designed for movement efficiency, storage capacity, and durability.",
    icon: "bi bi-box-seam",
  },
  {
    title: "Process And Utility Infrastructure",
    description: "Industrial civil work and support infrastructure built for continuity, access, and site control.",
    icon: "bi bi-gear-wide-connected",
  },
];

const industrialProjects: IndustrialProject[] = [
  {
    title: "Manufacturing Unit Setup",
    category: "Manufacturing Facility",
    location: "Amausi Industrial Area",
    description:
      "An industrial unit developed to support production workflows, worker movement, and dependable day-to-day operations.",
    features: [
      "Process-friendly layout with safety access routes",
      "Utility planning for equipment and support services",
      "Durable construction for continuous industrial use",
    ],
    images: [
      "https://images.unsplash.com/photo-1565793298595-6a879b1d9492?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?auto=format&fit=crop&w=1200&q=80",
    ],
  },
  {
    title: "Warehouse Development",
    category: "Warehouse Project",
    location: "Transport Nagar, Lucknow",
    description:
      "A large-span warehouse facility designed for efficient goods movement, organized storage, and robust industrial performance.",
    features: [
      "Storage-first planning with clear movement paths",
      "Heavy-duty flooring and utility support areas",
      "Reliable structure for large-volume operations",
    ],
    images: [
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?auto=format&fit=crop&w=1200&q=80",
    ],
  },
  {
    title: "Processing Plant Civil Work",
    category: "Process Infrastructure",
    location: "Bijnor Road, Lucknow",
    description:
      "Industrial civil work created for process-heavy operations with practical layouts, utility corridors, and service access.",
    features: [
      "Foundation and floor planning for plant operations",
      "Service corridor and utility movement support",
      "Execution aligned with industrial workflow needs",
    ],
    images: [
      "https://images.unsplash.com/photo-1565008447742-97f6f38c985c?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1581092160607-ee22731e6b43?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1513828583688-c52646db42da?auto=format&fit=crop&w=1200&q=80",
    ],
  },
];

const completedIndustrialProjects = [
  {
    title: "Completed Manufacturing Site",
    location: "Amausi",
    image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Finished Warehouse Bay",
    location: "Transport Nagar",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Industrial Utility Block",
    location: "Lucknow",
    image: "https://images.unsplash.com/photo-1581091215367-59ab6dcef10f?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Operational Floor Delivery",
    location: "Kanpur Road",
    image: "https://images.unsplash.com/photo-1565008447742-97f6f38c985c?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Warehouse Loading Zone",
    location: "NCR",
    image: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Completed Industrial Structure",
    location: "UP Industrial Belt",
    image: "https://images.unsplash.com/photo-1513828583688-c52646db42da?auto=format&fit=crop&w=1200&q=80",
  },
];

const IndustrialProjects: React.FC = () => {
  const { data: apiImages = [] } = useFetchPublicProjectImagesQuery("Industrial");

  const resolveImages = (fallbacks: string[], startIdx: number, count = 3): string[] => {
    const slice = apiImages.slice(startIdx, startIdx + count).map((img) => img.imageUrl);
    while (slice.length < count) slice.push(fallbacks[slice.length] ?? PLACEHOLDER);
    return slice;
  };

  const liveProjects = industrialProjects.map((p, i) => ({
    ...p,
    images: resolveImages(p.images, i * 3),
  }));

  const liveCompleted = completedIndustrialProjects.map((item, i) => ({
    ...item,
    image: apiImages[i]?.imageUrl ?? item.image,
  }));

  return (
    <section className='project-category-page website-page'>
      <div className='project-category-hero website-page-hero text-center'>
        <div className='container'>
          <p className='website-page-kicker mb-1'>Industrial Portfolio</p>
          <h1 className='project-page-title website-page-title mb-2'>
            Explore industrial projects built for durability, workflow efficiency, and dependable site operations.
          </h1>
          <p className='project-page-subtitle website-page-subtitle mx-auto mb-0'>
            Browse manufacturing, warehouse, and process infrastructure projects with detailed highlights, multiple
            images, and completed industrial work that helps customers understand our delivery capability.
          </p>
        </div>
      </div>

      <div className='container website-page-content'>
        <div className='row g-4 mb-5'>
          {categoryHighlights.map((item) => (
            <div className='col-md-6 col-lg-4' key={item.title}>
              <article className='project-category-link-card h-100'>
                <div className='project-category-link-icon'>
                  <i className={item.icon}></i>
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            </div>
          ))}
        </div>

        <div className='project-residential-section-head mb-4'>
          <span className='website-section-kicker'>Project Types</span>
          <h2 className='website-section-title mb-2'>Choose the industrial project type that fits your operational need.</h2>
          <p className='website-section-subtitle mb-0'>
            Each section highlights a different industrial project type with practical features, site details, and
            multiple project images.
          </p>
        </div>

        <div className='residential-project-list'>
          {liveProjects.map((project) => (
            <article className='residential-project-card' key={`${project.title}-${project.location}`}>
              <div className='residential-project-top'>
                <div>
                  <span className='residential-project-badge'>{project.category}</span>
                  <h3>{project.title}</h3>
                  <p className='residential-project-location'>
                    <i className='bi bi-geo-alt-fill me-2'></i>
                    {project.location}
                  </p>
                </div>
                <Link to={websitePaths.contact} className='btn brand-btn-primary'>
                  Discuss Similar Project
                </Link>
              </div>

              <div className='row g-4 align-items-start'>
                <div className='col-lg-7'>
                  <div className='residential-gallery-grid'>
                    <div className='residential-gallery-main'>
                      <img src={project.images[0]} alt={project.title} />
                    </div>
                    <div className='residential-gallery-stack'>
                      {project.images.slice(1).map((image, index) => (
                        <div className='residential-gallery-small' key={`${project.title}-${index}`}>
                          <img src={image} alt={`${project.title} view ${index + 2}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className='col-lg-5'>
                  <div className='residential-project-content'>
                    <p className='mb-3'>{project.description}</p>
                    <ul className='residential-project-features'>
                      {project.features.map((feature) => (
                        <li key={`${project.title}-${feature}`}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className='project-residential-section-head completed-projects-head'>
          <span className='website-section-kicker'>Completed Projects</span>
          <h2 className='website-section-title mb-2'>See completed industrial projects delivered for active work sites.</h2>
          <p className='website-section-subtitle mb-0'>
            This gallery showcases completed manufacturing, warehouse, and utility infrastructure work so customers can
            review the scale, finish, and practicality of our delivered industrial projects.
          </p>
        </div>

        <div className='row g-4'>
          {liveCompleted.map((item) => (
            <div className='col-sm-6 col-lg-4' key={`${item.title}-${item.location}`}>
              <article className='completed-project-card'>
                <img src={item.image} alt={item.title} className='completed-project-image' />
                <div className='completed-project-overlay'>
                  <h4>{item.title}</h4>
                  <p>{item.location}</p>
                </div>
              </article>
            </div>
          ))}
        </div>

        <div className='project-category-cta mt-4'>
          <div>
            <span className='website-section-kicker'>Plan Your Industrial Project</span>
            <h3 className='mb-2'>Tell us about your plant, warehouse, or site requirement and our team will guide you.</h3>
            <p className='mb-0'>
              We help customers plan industrial facilities, warehouse structures, and process-support spaces with a
              clear execution path.
            </p>
          </div>
          <Link to={websitePaths.contact} className='btn brand-btn-primary'>
            Start Industrial Consultation
          </Link>
        </div>
      </div>
    </section>
  );
};

export default IndustrialProjects;


